import { LucideIcon, ChevronRight } from 'lucide-react';
import { cn } from '@lib/utils';

export interface QuickAction {
  id: string;
  title: string;
  count: string;
  icon: LucideIcon;
  variant: 'purple' | 'orange' | 'green';
  onClick?: () => void;
}

interface QuickActionsProps {
  actions: QuickAction[];
}

const variantStyles = {
  purple: {
    iconBg: 'bg-[var(--bg-elevated)]',
    iconColor: 'text-[var(--text-accent)]',
  },
  orange: {
    iconBg: 'bg-[var(--state-warning-bg)]',
    iconColor: 'text-[var(--state-warning)]',
  },
  green: {
    iconBg: 'bg-[var(--state-success-bg)]',
    iconColor: 'text-[var(--state-success)]',
  },
};

export const QuickActions = ({ actions }: QuickActionsProps) => {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-soft">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Quick Actions</h2>
      </div>

      {/* Actions List */}
      <div className="space-y-3">
        {actions.map((action) => {
          const styles = variantStyles[action.variant];
          const Icon = action.icon;

          return (
            <button
              key={action.id}
              onClick={action.onClick}
              className="flex items-center gap-3 w-full rounded-2xl border border-[var(--border-subtle)] bg-white p-4 text-left transition-all duration-200 hover:border-[var(--border-default)] cursor-pointer hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              {/* Icon */}
              <div className={cn('rounded-xl p-2.5 shrink-0', styles.iconBg)}>
                <Icon className={cn('w-4 h-4', styles.iconColor)} strokeWidth={1.5} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-[var(--text-primary)]">
                  {action.title}
                </div>
                <div className="text-[12px] font-semibold text-[var(--text-muted)] bg-[var(--bg-base)] px-2.5 py-1 rounded-full inline-block mt-1">
                  {action.count}
                </div>
              </div>

              {/* Chevron */}
              <ChevronRight className="w-4 h-4 text-[var(--text-muted)] shrink-0" strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    </div>
  );
};